import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { isService1Tenant } from '../../Utils/ServiceProviders/lumin';
import { BASE_URL } from '../../Utils/url';
import moment from 'moment';
import 'moment-timezone';
import './GoodnightProcess.scss';

// ─── PRODUCTION values (restore before commit) ───────────────────
const WARN_MINS  = 18 * 60 + 45; // 6:45 PM ET  = 1125
const BLOCK_MINS = 19 * 60;      // 7:00 PM ET  = 1140
const END_MINS   = 21 * 60;      // 9:00 PM ET  = 1260
const FORCE_SHOW = false;
// ─────────────────────────────────────────────────────────────────

// ─── TEST values — dynamic from current ET time ──────────────────
// N = ET minutes right now. Swap ONE block at a time.
// FORCE_SHOW bypasses the Service1 tenant check on localhost.
// const FORCE_SHOW = true;
// const _N = (() => { const t = moment().tz('America/New_York'); return t.hours() * 60 + t.minutes(); })();

// ── UC1: warning banner (banner shows, blocks in ~3 min) ─────────
// const WARN_MINS  = _N;
// const BLOCK_MINS = _N + 3;
// const END_MINS   = _N + 20;

// ── UC2: blocked overlay (already in block window) ───────────────
// const WARN_MINS  = _N - 20;
// const BLOCK_MINS = _N - 5;
// const END_MINS   = _N + 15;

// ── UC3: done / refresh prompt (also set wasBlocked useState below)
// const WARN_MINS  = _N - 30;
// const BLOCK_MINS = _N - 25;
// const END_MINS   = _N - 5;

// ── normal: nothing shown ────────────────────────────────────────
// const WARN_MINS  = _N + 60;
// const BLOCK_MINS = _N + 75;
// const END_MINS   = _N + 90;
// ─────────────────────────────────────────────────────────────────

const getETMinutes = () => {
    const now = moment().tz('America/New_York');
    return now.hours() * 60 + now.minutes();
};

const getETSeconds = () => {
    const now = moment().tz('America/New_York');
    return now.hours() * 3600 + now.minutes() * 60 + now.seconds();
};

const calcPhase = () => {
    const t = getETMinutes();
    if (t >= WARN_MINS && t < BLOCK_MINS) return 'warning';
    if (t >= BLOCK_MINS && t < END_MINS)  return 'blocked';
    if (t >= END_MINS)                    return 'done';
    return 'normal';
};

const calcSecsLeft = () => Math.max(0, BLOCK_MINS * 60 - getETSeconds());

const GoodnightProcess = (props) => {
    const initialPhase = calcPhase();
    const [phase, setPhase] = useState(initialPhase);
    const [wasBlocked, setWasBlocked] = useState(initialPhase === 'blocked');
    const [secsLeft, setSecsLeft] = useState(calcSecsLeft);
    const [dismissed, setDismissed] = useState(false);

    // overlayRef   — points to the gn-overlay div React rendered
    // parentRef    — the overlay's actual DOM parent at render time; re-append here on tamper
    // observerRef  — MutationObserver instance, so we can disconnect it
    const overlayRef  = useRef(null);
    const parentRef   = useRef(null);
    const observerRef = useRef(null);

    const isEnabledTenant  = FORCE_SHOW || isService1Tenant(props);
    const tenantName  = props.state?.theme?.values?.tenantFullName;
    const tenantLogo  = `${BASE_URL.CDN}/tenants/${tenantName}/email/logo.png`;

    // Overlay is active for UC2 (blocked) and UC3 (done + wasBlocked)
    const overlayActive = isEnabledTenant && (phase === 'blocked' || (phase === 'done' && wasBlocked));

    // Ticker — every 1s: drives countdown seconds + phase transitions
    useEffect(() => {
        if (!isEnabledTenant) return;
        const id = setInterval(() => {
            const next = calcPhase();
            setPhase(next);
            if (next === 'blocked') setWasBlocked(true);
            if (next === 'warning') {
                setSecsLeft(calcSecsLeft());
                setDismissed(false);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [isEnabledTenant]);

    // Capture parent reference after every render so it's always fresh
    useEffect(() => {
        if (overlayRef.current) {
            parentRef.current = overlayRef.current.parentNode;
        }
    });

    // DOM tamper guard — imperatively re-appends the overlay to its original parent
    // when removed via DevTools. We never change `key` or trigger React reconciliation,
    // so React's removeChild is never called on a detached node.
    useEffect(() => {
        if (!overlayActive) {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            return;
        }

        const root = document.getElementById('root') || document.body;

        try {
            const observer = new MutationObserver(() => {
                try {
                    const el     = overlayRef.current;
                    const parent = parentRef.current;
                    if (!el || !parent) return;

                    if (!document.contains(el)) {
                        // Re-append to the exact parent React expects —
                        // React's reconciler uses this parent reference when unmounting,
                        // so we must restore to the same node, not body or root.
                        parent.appendChild(el);
                    }
                } catch (_) { /* never crash the observer callback */ }
            });

            observer.observe(root, { childList: true, subtree: true });
            observerRef.current = observer;
        } catch (_) { /* MutationObserver unsupported — degrade silently */ }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [overlayActive]);

    if (!isEnabledTenant) return null;

    // ── UC3: goodnight over, user was blocked ─────────────────────
    if (phase === 'done' && wasBlocked) {
        return (
            <div className="gn-overlay" ref={overlayRef}>
                <div className="gn-card">
                    <img src={tenantLogo} alt="" className="gn-logo" />
                    <div className="gn-title">We're back online!</div>
                    <div className="gn-body">
                        Please refresh your browser to continue.
                    </div>
                    <button className="gn-action-btn" onClick={() => window.location.reload()}>
                        Refresh Browser
                    </button>
                </div>
            </div>
        );
    }

    // ── UC2: full-screen blocker ──────────────────────────────────
    if (phase === 'blocked') {
        return (
            <div className="gn-overlay" ref={overlayRef}>
                <div className="gn-card">
                    <img src={tenantLogo} alt="" className="gn-logo" />
                    <div className="gn-title">We're taking a quick timeout!</div>
                    <div className="gn-body">
                        Temporarily unavailable. No need to refresh.<br />
                        Please try again later today.
                    </div>
                    <div className="gn-note">Available again after 9:00 PM ET</div>
                </div>
            </div>
        );
    }

    // ── UC1: 15-min pre-warning sticky banner ─────────────────────
    if (phase === 'warning' && !dismissed) {
        const mm = String(Math.floor(secsLeft / 60)).padStart(2, '0');
        const ss = String(secsLeft % 60).padStart(2, '0');
        return (
            <div className="gn-banner">
                <div className="gn-banner-inner">
                    <svg className="gn-banner-alert" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="12" cy="17" r="1" fill="currentColor"/>
                    </svg>
                    <div className="gn-banner-text">
                        <strong>Heads up!</strong>&nbsp; The app will be temporarily unavailable in&nbsp;
                        <span className="gn-countdown">{mm}:{ss}</span>.
                        &nbsp;Please complete any in-progress work.
                    </div>
                    <button className="gn-banner-close" onClick={() => setDismissed(true)} aria-label="Dismiss">✕</button>
                </div>
            </div>
        );
    }

    return null;
};

function mapStateToProps(state) {
    return { state };
}

export default connect(mapStateToProps)(GoodnightProcess);
